import { useState } from 'react';
import { LockOutlined } from '@mui/icons-material';
import DateRangePicker from '../../molecules/DateRangePicker/DateRangePicker';
import DepartmentPicker from '../../molecules/DepartmentPicker/DepartmentPicker';
import ForecastRuler from '../../molecules/ForecastRuler/ForecastRuler';
import { isDfmcLayerSelected } from '../../../utils/accessRightsHelpers';
import {
  CATALOG_COLORS,
  SERVICE_LABELS,
  LAYER_LABELS,
  COUNTRY_LABELS,
  FORECAST_MIN_DAY,
  FORECAST_MAX_DAY,
  FRANCE_COUNTRY_ID,
  AUTH_FORCED_LAYER_NAME,
} from '../../../constants/accessRightsConfig';
import './ModuleCard.css';

const ModuleCard = ({ catalogEntry, perm, onChange, isSuperAdmin, onRequestDisable, frenchDepartmentsCatalog = [] }) => {
  const {
    catalog_id, services, layers, countries, can_grant,
    available_layer_ids,
    available_country_ids,
    available_department_ids,
    available_date_start,
    available_date_end,
    available_interval_min_j,
    available_interval_max_j,
  } = catalogEntry;

  const service         = services?.[0] || {};
  const serviceName     = service.name || '';
  const color            = CATALOG_COLORS[catalog_id] || '#64748b';
  const label             = SERVICE_LABELS[serviceName] || serviceName;
  const isEnabled         = perm.enabled;
  const isLocked          = !can_grant && !isSuperAdmin;
  const isHistoric        = serviceName === 'historic_fire_service';
  const isWeather         = serviceName === 'weather';
  const isDisplayLayers   = serviceName === 'display_layers_service';
  const isAuth            = serviceName === 'auth';
  const isActiveFires     = serviceName === 'active_fires';

  const forcedAuthLayer = isAuth ? layers?.find(l => l.name === AUTH_FORCED_LAYER_NAME) : null;

  const availLayerIds   = available_layer_ids?.length      > 0 ? available_layer_ids      : null;
  const availCountryIds = available_country_ids?.length    > 0 ? available_country_ids    : null;
  const availDeptIds    = available_department_ids?.length > 0 ? available_department_ids : null;

  // Bornes dates disponibles pour le DateRangePicker
  const availDateStart = available_date_start ? new Date(available_date_start) : null;
  const availDateEnd   = available_date_end   ? new Date(available_date_end)   : null;

  // Bornes intervalle disponibles pour ForecastRuler
  const availMinJ = available_interval_min_j ?? FORECAST_MIN_DAY;
  const availMaxJ = available_interval_max_j ?? FORECAST_MAX_DAY;

  const isLayerAvailable   = (id) => !availLayerIds   || availLayerIds.includes(id);
  const isCountryAvailable = (id) => !availCountryIds || availCountryIds.includes(id);

  const franceCountry     = countries?.find(c => c.id === FRANCE_COUNTRY_ID);
  const frenchDepartments = franceCountry?.departements || [];

  // La France est "incluse" soit explicitement (dans selectedCountryIds),
  // soit implicitement via "Tous" (tableau vide = tous les pays).
  const isFranceIncluded =
    !perm.selectedCountryIds?.length || perm.selectedCountryIds?.includes(FRANCE_COUNTRY_ID);

  const showDeptPicker =
    isEnabled && !isAuth && isFranceIncluded && frenchDepartments.length > 0;

  // ── "Points chauds" n'a pas ses propres données pays/départements : on
  // utilise la liste de référence transmise par AccessRightsModal, purement
  // pour l'affichage. État local, jamais transmis à onChange/dirtyRef — donc
  // sans effet sur la sauvegarde, même si l'utilisateur clique dessus.
  const displayDepartments = frenchDepartments.length > 0 ? frenchDepartments : frenchDepartmentsCatalog;
  const [cosmeticDeptIds, setCosmeticDeptIds] = useState(() => displayDepartments.map(d => d.id));

  // ── Sur "Couches terrain", le ForecastRuler n'est pertinent QUE pour la
  // couche DFMC — il ne s'affiche que quand cette couche est sélectionnée.
  const isDfmcSelected = isDisplayLayers && isDfmcLayerSelected(catalogEntry, perm);
  const showForecastRuler = isEnabled && (isWeather || (isDisplayLayers && isDfmcSelected));

const toggleModule = () => {
  if (isLocked || isAuth) return;
  if (isEnabled) onRequestDisable(catalog_id, label);
  else onChange(catalog_id, 'enabled', true);
};

  const handleLayerToggle = (id) => {
    if (isAuth && forcedAuthLayer?.id === id) return;
    if (!isEnabled || isLocked || !isLayerAvailable(id)) return;
    const cur = perm.selectedLayerIds || [];
    onChange(catalog_id, 'selectedLayerIds',
      cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  // ── Sélection/désélection d'un pays individuel ──
  // Cas particulier France : quand on l'AJOUTE et qu'aucun département
  // n'est encore choisi, on coche visuellement tous les départements
  // (comme si "Tout" avait été cliqué) pour rester cohérent avec la
  // sémantique "France sélectionnée = tous ses départements par défaut".
  const handleCountryToggle = (id) => {
    if (!isEnabled || isLocked || !isCountryAvailable(id)) return;
    const cur = perm.selectedCountryIds || [];
    const isRemoving = cur.includes(id);
    onChange(catalog_id, 'selectedCountryIds',
      isRemoving ? cur.filter(x => x !== id) : [...cur, id]);

    if (id === FRANCE_COUNTRY_ID) {
      if (isRemoving) {
        onChange(catalog_id, 'selectedDepartmentIds', []);
      } else if (!perm.selectedDepartmentIds?.length) {
        const allDeptIds = availDeptIds?.length ? availDeptIds : frenchDepartments.map(d => d.id);
        onChange(catalog_id, 'selectedDepartmentIds', allDeptIds);
      }
    }
  };

  const handleSelectAllCountries = () => {
    if (isLocked || !isEnabled) return;
    onChange(catalog_id, 'selectedCountryIds', []);
    const allDeptIds = availDeptIds?.length ? availDeptIds : frenchDepartments.map(d => d.id);
    onChange(catalog_id, 'selectedDepartmentIds', allDeptIds);
  };

  // ── Changement de sélection des départements ──
  // Si l'utilisateur vide complètement la sélection (bouton "Aucun") alors
  // que la France était incluse, cela signifie "retirer la France" :
  // - on transforme le "Tous" implicite (pays) en liste explicite des
  //   AUTRES pays (tous sauf la France),
  // - ou, si la France était déjà explicite dans la liste, on l'en retire,
  // - et on vide bien les départements sélectionnés.
  const handleDepartmentsChange = (ids) => {
    if (!isEnabled || isLocked) return;

    if (ids.length === 0 && isFranceIncluded) {
      const allCountryIds = countries.map(c => c.id);
      const baseCountryIds = perm.selectedCountryIds?.length
        ? perm.selectedCountryIds
        : allCountryIds;
      const otherCountryIds = baseCountryIds.filter(cid => cid !== FRANCE_COUNTRY_ID);

      onChange(catalog_id, 'selectedCountryIds', otherCountryIds);
      onChange(catalog_id, 'selectedDepartmentIds', []);
      return;
    }

    onChange(catalog_id, 'selectedDepartmentIds', ids);
  };

  const handleForecastChange = (field, value) => {
    if (!isEnabled || isLocked) return;
    onChange(catalog_id, field, value);
  };

  // Clamp date pour ne pas dépasser les bornes disponibles
  // Clamp date pour ne pas dépasser les bornes disponibles + empêche dateStart > dateEnd
  const handleDateStartChange = (date) => {
    if (!date) { onChange(catalog_id, 'dateStart', null); return; }
    let d = date;
    if (availDateStart && d < availDateStart) d = availDateStart;
    if (availDateEnd   && d > availDateEnd)   d = availDateEnd;
    onChange(catalog_id, 'dateStart', d);
    if (perm.dateEnd && d > perm.dateEnd) {
      onChange(catalog_id, 'dateEnd', d);
    }
  };

  const handleDateEndChange = (date) => {
    if (!date) { onChange(catalog_id, 'dateEnd', null); return; }
    let d = date;
    if (availDateStart && d < availDateStart) d = availDateStart;
    if (availDateEnd   && d > availDateEnd)   d = availDateEnd;
    if (perm.dateStart && d < perm.dateStart) d = perm.dateStart;
    onChange(catalog_id, 'dateEnd', d);
  };

  return (
    <div
      className={[
        'arm-module-card',
        isEnabled ? 'arm-module-card--active' : '',
        isLocked  ? 'arm-module-card--locked'  : '',
      ].filter(Boolean).join(' ')}
      style={{ '--module-color': color }}
    >
      {/* En-tête */}
      <div className="arm-module-header">
        <div className="arm-module-title">
          <span className="arm-module-dot" style={{ background: color }} />
          <span className="arm-module-name">{label}</span>
        </div>
  <button
  type="button"
  className={['arm-toggle', isEnabled ? 'arm-toggle--on' : '', (isLocked || isAuth) ? 'arm-toggle--disabled' : ''].filter(Boolean).join(' ')}
  style={isEnabled && !isLocked ? { '--toggle-color': color } : {}}
  onClick={toggleModule}
  disabled={isLocked || isAuth}
  aria-label={isEnabled ? 'Désactiver' : 'Activer'}
  title={isAuth ? 'Authentification toujours active (requis)' : undefined}
>
  <span className="arm-toggle-thumb" />
</button>
      </div>

      {/* Corps */}
      <div className={`arm-module-body ${!isEnabled ? 'arm-module-body--disabled' : ''}`}>

        {/* LAYERS */}
        {layers.length > 0 && (
          <>
            <div className="arm-section-label">
              Couches
              {isEnabled && (perm.selectedLayerIds?.length || 0) > 0 && (
                <span className="arm-selection-count">{perm.selectedLayerIds.length}/{layers.length}</span>
              )}
            </div>
            <div className="arm-layers">
              {!isAuth && (
                <button type="button"
                  className={`arm-layer-chip ${!perm.selectedLayerIds?.length ? 'arm-layer-chip--active' : ''}`}
                  onClick={() => !isLocked && isEnabled && onChange(catalog_id, 'selectedLayerIds', [])}
                  disabled={!isEnabled || isLocked}
                  style={!perm.selectedLayerIds?.length ? { '--chip-color': color } : {}}
                >Toutes</button>
              )}
              {layers.map((layer) => {
                const isSelected = perm.selectedLayerIds?.includes(layer.id);
                const available  = isLayerAvailable(layer.id);
                const isForced   = isAuth && forcedAuthLayer?.id === layer.id;
                return (
                  <button type="button" key={layer.id}
                    className={[
                      'arm-layer-chip',
                      isSelected              ? 'arm-layer-chip--active'      : '',
                      !available && !isForced ? 'arm-layer-chip--unavailable' : '',
                      isForced                ? 'arm-layer-chip--forced'      : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleLayerToggle(layer.id)}
                    disabled={!isEnabled || isLocked || !available || isForced}
                    style={isSelected && available ? { '--chip-color': color } : {}}
                    title={
                      isForced   ? 'Requis — toujours actif'             :
                      !available ? 'Non disponible pour cet utilisateur' :
                      undefined
                    }
                  >
                    {LAYER_LABELS[layer.name] || layer.name}
                    {isForced && <LockOutlined className="arm-layer-chip-lock-icon" />}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* PAYS — masqué pour auth */}
        {countries.length > 0 && !isAuth && (
          <>
            <div className="arm-section-label">
              PAYS
              {isEnabled && (perm.selectedCountryIds?.length || 0) > 0 && (
                <span className="arm-selection-count">{perm.selectedCountryIds.length}/{countries.length}</span>
              )}
            </div>
            <div className="arm-layers">
              <button type="button"
                className={`arm-layer-chip ${!perm.selectedCountryIds?.length ? 'arm-layer-chip--active' : ''}`}
                onClick={handleSelectAllCountries}
                disabled={!isEnabled || isLocked}
                style={!perm.selectedCountryIds?.length ? { '--chip-color': color } : {}}
              >Tous</button>
              {countries.map((c) => {
                const isSelected = perm.selectedCountryIds?.includes(c.id);
                const available  = isCountryAvailable(c.id);
                return (
                  <button type="button" key={c.id}
                    className={[
                      'arm-layer-chip',
                      isSelected ? 'arm-layer-chip--active'      : '',
                      !available ? 'arm-layer-chip--unavailable' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleCountryToggle(c.id)}
                    disabled={!isEnabled || isLocked || !available}
                    style={isSelected && available ? { '--chip-color': color } : {}}
                    title={!available ? 'Non disponible pour cet utilisateur' : (COUNTRY_LABELS[c.name] || c.name)}
                  >{COUNTRY_LABELS[c.name] || c.name}</button>
                );
              })}
            </div>
          </>
        )}

        {/* DÉPARTEMENTS FRANCE — masqué pour auth */}
        {showDeptPicker && (
          <>
            <div className="arm-section-label">
              DÉPARTEMENTS · FRANCE
              {(perm.selectedDepartmentIds?.length || 0) > 0 && (
                <span className="arm-selection-count">{perm.selectedDepartmentIds.length}</span>
              )}
            </div>
            <DepartmentPicker
              departments={frenchDepartments}
              selectedIds={perm.selectedDepartmentIds || []}
              onChange={handleDepartmentsChange}
              disabled={!isEnabled || isLocked}
              availableIds={availDeptIds}
            />
          </>
        )}

        {/* DÉPARTEMENTS FRANCE — affichage statique pour "Points chauds"
            (module sans données pays/départements réelles). On réutilise le
            même DepartmentPicker que les autres cartes, tous départements
            pré-cochés, mais branché sur un état local (cosmeticDeptIds) —
            jamais sur onChange/dirtyRef. Même si l'utilisateur clique et
            change la sélection ici, c'est purement visuel et ce n'est pas
            pris en compte à la sauvegarde.
            Pas de garde sur isEnabled : comme "Couches"/"PAYS", le bloc
            reste affiché quand le module est désactivé — c'est le style
            .arm-module-body--disabled du parent qui l'estompe, au lieu
            de le faire disparaître. */}
        {isActiveFires && !showDeptPicker && displayDepartments.length > 0 && (
          <>
            <div className="arm-section-label">
              DÉPARTEMENTS · FRANCE
              <span className="arm-selection-count">{cosmeticDeptIds.length}</span>
            </div>
            <DepartmentPicker
              departments={displayDepartments}
              selectedIds={cosmeticDeptIds}
              onChange={setCosmeticDeptIds}
              disabled={!isEnabled || isLocked}
              availableIds={null}
            />
          </>
        )}

        {/* HISTORIQUE — DateRangePicker avec bornes disponibles */}
        {isHistoric && isEnabled && (
          <>
            <div className="arm-section-label">
              PÉRIODE D'ACCÈS
              {availDateStart && availDateEnd && (
                <span className="arm-section-hint">
                  ({availDateStart.toLocaleDateString('fr-FR')} → {availDateEnd.toLocaleDateString('fr-FR')})
                </span>
              )}
            </div>
            <DateRangePicker
              startDate={perm.dateStart}
              endDate={perm.dateEnd}    
              onStartChange={handleDateStartChange}
              onEndChange={handleDateEndChange}
              minDate={availDateStart}
              maxDate={availDateEnd}
            />  
          </>
        )}

        {/* MÉTÉO + COUCHES TERRAIN (DFMC uniquement) — même ForecastRuler,
            réglage partagé. Sur "Couches terrain", ne s'affiche que si la
            couche DFMC est sélectionnée. Modifier l'intervalle sur l'une
            des deux cartes met à jour l'autre (sync dans AccessRightsModal). */}
        {showForecastRuler && (
          <>
            <div className="arm-section-label">
              INTERVALLE DE PRÉVISION
              <span className="arm-section-hint">
                (partagé avec {isWeather ? 'Couches terrain · DFMC' : 'Météo'})
              </span>
            </div>
            <ForecastRuler
              minVal={perm.intervalMinJ}
              maxVal={perm.intervalMaxJ}
              availableMinJ={availMinJ}
              availableMaxJ={availMaxJ}
              onChange={handleForecastChange}
              disabled={!isEnabled || isLocked}
            />
          </>
        )}
      </div>

     {isLocked && !isAuth && (
  <div className="arm-module-locked-overlay">
    <LockOutlined />
    <span>Non disponible — vous ne possédez pas ce droit</span>
  </div>
)}
    </div>
  );
};

export default ModuleCard;